import React, { useMemo } from 'react';
import { X, Layout, Check } from 'lucide-react';
import { SECTION_TEMPLATES } from './sectionLayouts';
import { getLayoutVariants, getCurrentLayout } from './layoutVariants';

/**
 * Slide-in panel showing TWO layout-customization options for the selected block:
 *
 * 1) "Switch layout" (top section) — change the LAYOUT VARIANT of the same
 *    block type (e.g. gallery: grid ↔ masonry ↔ lookbook ↔ carousel).
 *    Content is preserved; only the visual layout changes.
 *
 * 2) "Rearrange section" (existing) — swap the entire section out for a
 *    different SECTION_TEMPLATE composition.
 */
export default function LayoutsPanel({ block, open, onClose, onApply, onChangeLayout }) {
  const variants = useMemo(() => block ? variantsForBlock(block) : [], [block]);
  const layoutVariants = useMemo(() => block ? getLayoutVariants(block.type) : [], [block]);
  const currentLayout = block ? getCurrentLayout(block) : null;
  if (!open || !block) return null;

  return (
    <div className="fixed top-[64px] right-6 z-[150] w-[420px] max-h-[calc(100vh-90px)] bg-[#15152E] border border-white/[0.08] rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col" data-testid="layouts-panel">
      <div className="px-5 pt-4 pb-3 border-b border-white/[0.06] flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold">Customize</p>
          <p className="text-white font-bold text-[13px] mt-0.5">Section Layouts</p>
          <p className="text-[10.5px] text-[#9CA3AF] mt-0.5">Switch this block's visual layout, or swap the whole section.</p>
        </div>
        <button onClick={onClose} className="text-[#9CA3AF] hover:text-white p-1" data-testid="layouts-panel-close"><X size={14} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-5 sqs-scrollbar">

        {/* Per-block layout switcher */}
        {layoutVariants.length > 1 && (
          <div data-testid="layout-switcher">
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold flex items-center gap-1.5">
                <Layout size={11} /> Switch layout
              </p>
              <span className="text-[10px] text-[#64748B]">{layoutVariants.length} variants</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {layoutVariants.map(v => {
                const active = currentLayout === v.id || (!currentLayout && v.id === layoutVariants[0].id);
                return (
                  <button
                    key={v.id}
                    onClick={() => onChangeLayout && onChangeLayout(block.id, v.id)}
                    className={`relative text-left rounded-lg p-3 border transition-all ${
                      active
                        ? 'border-[#A78BFA] bg-[#A78BFA]/10 shadow-[0_0_18px_rgba(167,139,250,0.25)]'
                        : 'border-white/[0.08] bg-white/[0.025] hover:border-[#A78BFA]/50 hover:bg-white/[0.045]'
                    }`}
                    data-testid={`layout-variant-pill-${v.id}`}
                  >
                    {active && (
                      <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-[#A78BFA] flex items-center justify-center">
                        <Check size={9} className="text-white" strokeWidth={3} />
                      </span>
                    )}
                    <LayoutThumb variantId={v.id} blockType={block.type} active={active} />
                    <p className="text-[12px] font-bold text-white mt-2">{v.label}</p>
                    <p className="text-[10px] text-[#9CA3AF] mt-0.5 leading-snug">{v.hint}</p>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#64748B] mt-2.5">
              Switching layout keeps your content — only the visual arrangement changes.
            </p>
          </div>
        )}

        {/* Divider between switcher and rearrange */}
        {layoutVariants.length > 1 && variants.length > 0 && (
          <div className="border-t border-white/[0.06] pt-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold mb-2.5">Rearrange section</p>
          </div>
        )}

        {/* Existing section-swap variants */}
        {variants.length === 0 && layoutVariants.length <= 1 && (
          <p className="text-[12px] text-[#9CA3AF] py-6 text-center">
            No alternate layouts available for this section type yet.
          </p>
        )}
        {variants.map(v => (
          <button
            key={v.id}
            onClick={() => onApply(v)}
            className="w-full text-left rounded-lg border-2 border-white/[0.06] hover:border-[#A78BFA] hover:shadow-[0_0_16px_rgba(167,139,250,0.2)] transition-all overflow-hidden bg-[#0E0E23]"
            data-testid={`layout-variant-${v.id}`}
          >
            <div className="aspect-[16/8] bg-[#0E0E23] relative overflow-hidden">
              {v.preview && typeof v.preview === 'string' && v.preview.startsWith('http') ? (
                <img src={v.preview} alt={v.label} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#1F1F3D] to-[#0E0E23]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
              <span className="absolute bottom-2 left-2 text-[9px] tracking-[0.18em] font-bold uppercase text-white bg-black/40 px-2 py-0.5 rounded">
                {(v.blocks || []).map(b => b.type).slice(0, 3).join(' · ')}
              </span>
            </div>
            <div className="px-4 py-2.5">
              <p className="text-[12.5px] font-bold text-white">{v.label}</p>
            </div>
          </button>
        ))}
      </div>
      <div className="px-5 py-3 border-t border-white/[0.06] text-[10px] text-[#64748B]">
        Switching layout preserves your content. Rearrange section replaces this block group.
      </div>
    </div>
  );
}

/** Tiny SVG thumbnail showing the visual idea of each variant. */
function LayoutThumb({ variantId, blockType, active }) {
  const stroke = active ? '#A78BFA' : '#64748B';
  const fill = active ? 'rgba(167,139,250,0.25)' : 'rgba(255,255,255,0.04)';
  return (
    <div className="aspect-[16/9] rounded-md bg-[#0E0E23] border border-white/[0.05] overflow-hidden p-1.5">
      <svg viewBox="0 0 64 36" className="w-full h-full">
        {renderThumbShape(blockType, variantId, { stroke, fill })}
      </svg>
    </div>
  );
}

function renderThumbShape(blockType, variantId, { stroke, fill }) {
  const s = stroke;
  const f = fill;

  if (blockType === 'gallery') {
    if (variantId === 'masonry') {
      return (
        <>
          <rect x="2"  y="2"  width="18" height="22" fill={f} stroke={s} strokeWidth=".7" />
          <rect x="22" y="2"  width="18" height="14" fill={f} stroke={s} strokeWidth=".7" />
          <rect x="42" y="2"  width="20" height="18" fill={f} stroke={s} strokeWidth=".7" />
          <rect x="22" y="18" width="18" height="16" fill={f} stroke={s} strokeWidth=".7" />
          <rect x="42" y="22" width="20" height="12" fill={f} stroke={s} strokeWidth=".7" />
          <rect x="2"  y="26" width="18" height="8"  fill={f} stroke={s} strokeWidth=".7" />
        </>
      );
    }
    if (variantId === 'lookbook') {
      return (
        <>
          <rect x="2"  y="2"  width="30" height="32" fill={f} stroke={s} strokeWidth=".7" />
          <rect x="34" y="2"  width="28" height="15" fill={f} stroke={s} strokeWidth=".7" />
          <rect x="34" y="19" width="28" height="15" fill={f} stroke={s} strokeWidth=".7" />
        </>
      );
    }
    if (variantId === 'horizontal-scroll') {
      return (
        <>
          {[2, 17, 32, 47].map((x) => (
            <rect key={x} x={x} y="10" width="13" height="20" fill={f} stroke={s} strokeWidth=".7" />
          ))}
          <line x1="2" y1="34" x2="62" y2="34" stroke={s} strokeWidth=".5" strokeDasharray="2 2" />
        </>
      );
    }
    // grid-uniform default
    return (
      <>
        {[0, 1, 2].map((col) => [0, 1].map((row) => (
          <rect key={`${col}-${row}`} x={2 + col * 21} y={2 + row * 17} width="18" height="14" fill={f} stroke={s} strokeWidth=".7" />
        )))}
      </>
    );
  }

  if (blockType === 'split') {
    if (variantId === 'image-left') return (
      <>
        <rect x="2"  y="4" width="28" height="28" fill={f} stroke={s} strokeWidth=".7" />
        <line x1="34" y1="10" x2="60" y2="10" stroke={s} strokeWidth="1" />
        <line x1="34" y1="16" x2="56" y2="16" stroke={s} strokeWidth=".7" />
        <line x1="34" y1="20" x2="58" y2="20" stroke={s} strokeWidth=".7" />
      </>
    );
    if (variantId === 'overlapping-image') return (
      <>
        <rect x="4"  y="4"  width="22" height="22" fill={f} stroke={s} strokeWidth=".7" />
        <rect x="16" y="14" width="22" height="20" fill={f} stroke={s} strokeWidth=".7" />
        <line x1="42" y1="10" x2="60" y2="10" stroke={s} strokeWidth="1" />
        <line x1="42" y1="16" x2="58" y2="16" stroke={s} strokeWidth=".7" />
      </>
    );
    if (variantId === 'asymmetric') return (
      <>
        <rect x="34" y="2" width="28" height="32" fill={f} stroke={s} strokeWidth=".7" />
        <line x1="2" y1="14" x2="28" y2="14" stroke={s} strokeWidth="1" />
        <line x1="2" y1="20" x2="24" y2="20" stroke={s} strokeWidth=".7" />
      </>
    );
    // image-right default
    return (
      <>
        <line x1="4" y1="10" x2="28" y2="10" stroke={s} strokeWidth="1" />
        <line x1="4" y1="16" x2="26" y2="16" stroke={s} strokeWidth=".7" />
        <line x1="4" y1="20" x2="28" y2="20" stroke={s} strokeWidth=".7" />
        <rect x="34" y="4" width="28" height="28" fill={f} stroke={s} strokeWidth=".7" />
      </>
    );
  }

  if (blockType === 'hero') {
    if (variantId === 'centered-minimal') return (
      <>
        <line x1="18" y1="14" x2="46" y2="14" stroke={s} strokeWidth="1.4" />
        <line x1="22" y1="20" x2="42" y2="20" stroke={s} strokeWidth=".6" />
        <rect x="26" y="24" width="12" height="3" fill={s} opacity=".6" />
      </>
    );
    if (variantId === 'split-left' || variantId === 'split-right') {
      const imgRight = variantId === 'split-left';
      return (
        <>
          <rect x={imgRight ? 34 : 2}  y="3" width="28" height="30" fill={f} stroke={s} strokeWidth=".7" />
          <line x1={imgRight ? 4  : 36} y1="10" x2={imgRight ? 28 : 60} y2="10" stroke={s} strokeWidth="1" />
          <line x1={imgRight ? 4  : 36} y1="16" x2={imgRight ? 24 : 56} y2="16" stroke={s} strokeWidth=".6" />
        </>
      );
    }
    if (variantId === 'layered-editorial') return (
      <>
        <rect x="3"  y="3"  width="40" height="30" fill={f} stroke={s} strokeWidth=".7" />
        <rect x="30" y="14" width="32" height="20" fill={f} stroke={s} strokeWidth=".7" />
        <line x1="6" y1="9" x2="20" y2="9" stroke={s} strokeWidth="1" />
      </>
    );
    if (variantId === 'video-hero') return (
      <>
        <rect x="2" y="2" width="60" height="32" fill={f} stroke={s} strokeWidth=".7" />
        <polygon points="28,12 28,24 40,18" fill={s} />
      </>
    );
    // cinematic-fullwidth default
    return (
      <>
        <rect x="2" y="2" width="60" height="32" fill={f} stroke={s} strokeWidth=".7" />
        <line x1="10" y1="20" x2="40" y2="20" stroke={s} strokeWidth="1.4" />
        <line x1="14" y1="25" x2="32" y2="25" stroke={s} strokeWidth=".6" />
      </>
    );
  }

  if (blockType === 'stats') {
    if (variantId === 'kpi-cards') return (
      <>
        {[2, 17, 32, 47].map((x) => (
          <rect key={x} x={x} y="8" width="13" height="20" fill={f} stroke={s} strokeWidth=".7" />
        ))}
      </>
    );
    if (variantId === 'centered-counter') return (
      <>
        <text x="32" y="22" fontSize="14" fill={s} textAnchor="middle" fontWeight="bold">25K</text>
        <line x1="22" y1="28" x2="42" y2="28" stroke={s} strokeWidth=".6" />
      </>
    );
    // kpi-row default
    return (
      <>
        {[6, 22, 38, 54].map((x) => (
          <g key={x}>
            <line x1={x - 4} y1="14" x2={x + 4} y2="14" stroke={s} strokeWidth="1.2" />
            <line x1={x - 4} y1="20" x2={x + 4} y2="20" stroke={s} strokeWidth=".5" />
          </g>
        ))}
      </>
    );
  }

  if (['testimonials', 'services', 'features', 'team', 'cta'].includes(blockType)) {
    if (variantId.includes('banner') || variantId.includes('large-banner')) return (
      <>
        <rect x="2" y="6" width="60" height="24" fill={f} stroke={s} strokeWidth=".7" />
        <line x1="14" y1="16" x2="50" y2="16" stroke={s} strokeWidth="1" />
        <line x1="20" y1="22" x2="44" y2="22" stroke={s} strokeWidth=".6" />
      </>
    );
    if (variantId.includes('carousel')) return (
      <>
        <rect x="-6" y="8" width="20" height="20" fill={f} stroke={s} strokeWidth=".7" />
        <rect x="20" y="6" width="24" height="24" fill={f} stroke={s} strokeWidth="1" />
        <rect x="50" y="8" width="20" height="20" fill={f} stroke={s} strokeWidth=".7" />
      </>
    );
    if (variantId.includes('stacked') || variantId.includes('alternating')) return (
      <>
        <rect x="2" y="3"  width="60" height="9" fill={f} stroke={s} strokeWidth=".7" />
        <rect x="2" y="14" width="60" height="9" fill={f} stroke={s} strokeWidth=".7" />
        <rect x="2" y="25" width="60" height="9" fill={f} stroke={s} strokeWidth=".7" />
      </>
    );
    if (variantId.includes('spotlight') || variantId.includes('image-cta')) return (
      <>
        <rect x="2" y="4" width="26" height="28" fill={f} stroke={s} strokeWidth=".7" />
        <line x1="32" y1="10" x2="60" y2="10" stroke={s} strokeWidth="1" />
        <line x1="32" y1="16" x2="56" y2="16" stroke={s} strokeWidth=".6" />
        <rect x="32" y="22" width="14" height="4" fill={s} opacity=".7" />
      </>
    );
    // three-up / grid / cards default
    return (
      <>
        {[2, 23, 44].map((x) => (
          <rect key={x} x={x} y="6" width="18" height="24" fill={f} stroke={s} strokeWidth=".7" />
        ))}
      </>
    );
  }

  if (blockType === 'editorial') {
    if (variantId === 'pull-quote') return (
      <>
        <text x="6" y="20" fontSize="22" fill={s} fontWeight="bold">"</text>
        <line x1="16" y1="14" x2="56" y2="14" stroke={s} strokeWidth="1" />
        <line x1="16" y1="20" x2="50" y2="20" stroke={s} strokeWidth=".7" />
        <line x1="16" y1="24" x2="54" y2="24" stroke={s} strokeWidth=".7" />
      </>
    );
    if (variantId === 'two-column-manifesto') return (
      <>
        <line x1="4"  y1="8"  x2="28" y2="8"  stroke={s} strokeWidth=".7" />
        <line x1="4"  y1="14" x2="26" y2="14" stroke={s} strokeWidth=".7" />
        <line x1="4"  y1="20" x2="28" y2="20" stroke={s} strokeWidth=".7" />
        <line x1="36" y1="8"  x2="60" y2="8"  stroke={s} strokeWidth=".7" />
        <line x1="36" y1="14" x2="58" y2="14" stroke={s} strokeWidth=".7" />
        <line x1="36" y1="20" x2="60" y2="20" stroke={s} strokeWidth=".7" />
      </>
    );
    return (
      <>
        <line x1="14" y1="14" x2="50" y2="14" stroke={s} strokeWidth="1.2" />
        <line x1="18" y1="20" x2="46" y2="20" stroke={s} strokeWidth=".7" />
        <line x1="22" y1="25" x2="42" y2="25" stroke={s} strokeWidth=".5" />
      </>
    );
  }

  // Default fallback
  return (
    <>
      <rect x="2" y="6" width="60" height="24" fill={f} stroke={s} strokeWidth=".7" />
      <line x1="10" y1="14" x2="40" y2="14" stroke={s} strokeWidth=".7" />
      <line x1="10" y1="20" x2="50" y2="20" stroke={s} strokeWidth=".5" />
    </>
  );
}

// ─────────────────────────── Helpers ───────────────────────────

const TYPE_TO_CATEGORIES = {
  hero:          ['introduce'],
  marquee:       ['introduce'],
  about:         ['introduce', 'content-memberships'],
  banner:        ['introduce', 'group-events', 'services'],
  custom_text:   ['introduce'],
  fluid_canvas:  ['introduce'],
  products:      ['services', 'content-memberships'],
  donations:     ['donations'],
  booking:       ['scheduling'],
  testimonial:   ['testimonials'],
  form:          ['forms', 'services', 'group-events'],
  gallery:       ['images', 'portfolio', 'group-events'],
  video:         ['videos'],
  signup:        ['newsletter'],
  audio:         ['audio'],
  embed:         ['social'],
  custom_image:  ['images'],
  single_image:  ['images'],
};

function variantsForBlock(block) {
  const cats = TYPE_TO_CATEGORIES[block.type] || [];
  const all = [];
  cats.forEach(cat => {
    (SECTION_TEMPLATES[cat] || []).forEach(t => all.push(t));
  });
  const seen = new Set();
  return all.filter(v => { if (seen.has(v.id)) return false; seen.add(v.id); return true; });
}
